import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { BsCheckCircle } from "react-icons/bs";
import {
  CustomInput,
  CustomFormField,
} from "@shared/components";
import ModalPopup from "@components/Common/ModalPopup";

/**
 * Reset Password Modal Component
 * Memoized to prevent unnecessary re-renders
 */
export const ResetPasswordModal = memo(({
  show,
  onClose,
  emailId,
  setEmailId,
  validationError,
  setValidationError,
  onReset,
}) => {
  // Memoize email change handler
  const handleEmailChange = useCallback((e) => {
    setEmailId(e.target.value);
    setValidationError("");
  }, [setEmailId, setValidationError]);

  return (
    <ModalPopup
      className="max-w-md"
      isModalOpen={show}
      setIsModalOpen={onClose}
      backdrop={true}
      fade={false}
      data={
        <div className="p-6">
          <h6 className="text-xl font-semibold mb-3 text-gray-800">Reset Password</h6>
          <p className="text-sm text-gray-600 mb-6">Please enter your valid Pidilite Email ID</p>
          
          <form onSubmit={onReset} className="space-y-5">
            <CustomFormField
              error={validationError}
              helperText={validationError}
            >
              <CustomInput
                type="email"
                value={emailId}
                placeholder="Enter Email ID"
                onChange={handleEmailChange}
                error={!!validationError}
                fullWidth
              />
            </CustomFormField>

            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-[#3901FF] hover:text-[#2d01cc] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3901FF] focus:ring-offset-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={onReset}
                className="px-6 py-2 text-sm font-medium text-white bg-[#3901FF] hover:bg-[#2d01cc] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3901FF] focus:ring-offset-2 rounded-lg"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      }
    />
  );
});

ResetPasswordModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  emailId: PropTypes.string.isRequired,
  setEmailId: PropTypes.func.isRequired,
  validationError: PropTypes.string.isRequired,
  setValidationError: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

/**
 * Success Modal Component
 * Memoized to prevent unnecessary re-renders
 */
export const SuccessModal = memo(({ show, onClose, emailId }) => (
  <ModalPopup
    className="text-center"
    isModalOpen={show}
    setIsModalOpen={onClose}
    title="Reset Password"
    data={
      <p className="mt-2">
        Password Reset link has been successfully sent to your email address{" "}
        <b>{emailId}</b>
      </p>
    }
    icon={
      <div className="self-center mt-8 flex items-center justify-center">
        <BsCheckCircle className="text-2xl text-[#FFA60033] mt-4" />
        <BsCheckCircle className="text-5xl text-[#2CC26A] mx-2" />
        <BsCheckCircle className="text-2xl text-[#FFA60033] mt-4" />
      </div>
    }
  />
));

SuccessModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  emailId: PropTypes.string.isRequired,
};
